import { useEffect, useEffectEvent, useRef } from "react";
import { usePage } from "@inertiajs/react";
import { useStreamSelector, type StreamSnapshot, type useLanggraphStreamOptions } from "@launch10/use-langgraph-stream";
import type { InertiaProps, InsightsGraphState } from "@shared";
import { useInsightsStore, useStoredInsights } from "@stores/insightsStore";
import { useProjectStreamOptions } from "@hooks/useProjectStreamOptions";

// Use the same types as the Dashboard page
type DashboardProps =
  InertiaProps.paths["/dashboard"]["get"]["responses"]["200"]["content"]["application/json"];
type Insight = NonNullable<DashboardProps["insights"]>[number];

interface InsightsPageProps {
  insights: Insight[] | null;
  metrics_summary: DashboardProps["metrics_summary"];
  [key: string]: unknown;
}

export type InsightsSnapshot = StreamSnapshot<InsightsGraphState>;

/**
 * Stream options for a headless Chat.Root — connects to the insights graph.
 */
export function useInsightsStreamOptions(): useLanggraphStreamOptions<InsightsGraphState> {
  return useProjectStreamOptions<InsightsGraphState>({
    assistantId: "insights",
    onUpdateEvent: false,
    getInitialThreadId: () => undefined,
  });
}

/**
 * Typed selector into the insights stream snapshot.
 * Must be used inside a Chat.Root provider.
 */
export function useInsightsSelector<T>(
  selector: (snapshot: InsightsSnapshot) => T
): T {
  return useStreamSelector<T, InsightsGraphState>(selector);
}

export function useInsightsState() {
  return useInsightsSelector((s) => s.values?.insights);
}

export function useInsightsIsLoading() {
  return useInsightsSelector((s) => s.isLoading);
}

export function useInsightsError() {
  return useInsightsSelector((s) => s.values?.error);
}

/**
 * Auto-trigger insights generation on dashboard load.
 *
 * Priority: server > stored > langgraph > null
 * Uses insightsStore to survive browser back/forward navigation.
 * Must be used inside a Chat.Root provider.
 */
export function useInsightsAutoInit() {
  const { insights: serverInsights } = usePage<InsightsPageProps>().props;

  const submit = useInsightsSelector((s) => s.submit);
  const langgraphInsights = useInsightsState();
  const isLoading = useInsightsIsLoading();
  const error = useInsightsError();

  // Store for surviving back/forward navigation
  const storedInsights = useStoredInsights();
  const setStoredInsights = useInsightsStore((s) => s.setInsights);

  const isGenerating = useRef(false);

  // Check if we already have fresh insights from Rails
  const hasFreshServerInsights = serverInsights && serverInsights.length > 0;
  const hasStoredInsights = storedInsights && storedInsights.length > 0;

  const maybeGenerateInsights = useEffectEvent(() => {
    // Already generating - don't duplicate
    if (isGenerating.current) return;

    // Rails sent fresh insights - no need to talk to Langgraph
    if (hasFreshServerInsights) return;

    // We have stored insights from this session - no need to regenerate
    if (hasStoredInsights) return;

    isGenerating.current = true;

    // Trigger Langgraph insight generation with empty state submit
    submit({}).finally(() => {
      isGenerating.current = false;
    });
  });

  useEffect(() => {
    maybeGenerateInsights();
  }, [hasFreshServerInsights, hasStoredInsights]);

  // Store insights when Langgraph generates them
  const hasLanggraphInsights = langgraphInsights && langgraphInsights.length > 0;
  useEffect(() => {
    if (hasLanggraphInsights) {
      setStoredInsights(langgraphInsights);
    }
  }, [hasLanggraphInsights, langgraphInsights, setStoredInsights]);

  // Priority: server > stored > langgraph > null
  const insights = hasFreshServerInsights
    ? serverInsights
    : hasStoredInsights
      ? storedInsights
      : hasLanggraphInsights
        ? langgraphInsights
        : null;

  return {
    insights,
    isGenerating: isLoading,
    error: error?.message ?? null,
  };
}
